import SideNavigation, { SideNavigationProps } from '@cloudscape-design/components/side-navigation';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { canAccessScorecardAdmin } from '../../auth/scorecard-admin-access';
import { useAuth } from '../../auth/useAuth';

export function dashboardActiveHref(pathname: string): string {
  if (pathname === '/dashboard/voice-sales' || pathname === '/dashboard/voice-sales/') {
    return '/dashboard/voice-sales';
  }
  if (pathname.startsWith('/dashboard/voice-sales/campaigns')) {
    return '/dashboard/voice-sales/campaigns';
  }
  if (pathname.startsWith('/dashboard/voice-sales/calls')) {
    return '/dashboard/voice-sales/calls';
  }
  if (pathname.startsWith('/dashboard/voice-sales/agent')) {
    return '/dashboard/voice-sales/agent';
  }
  return pathname;
}

export function DashboardSideNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const canManageScorecards = canAccessScorecardAdmin(user?.email);

  const sideItems: SideNavigationProps.Item[] = [
    {
      type: 'section',
      text: 'Reviews',
      items: [
        { type: 'link', text: 'Places', href: '/dashboard/places' },
        { type: 'link', text: 'Contacts', href: '/dashboard/contacts' },
        { type: 'link', text: 'Lists', href: '/dashboard/lists' },
      ],
    },
    { type: 'divider' },
    {
      type: 'section',
      text: 'Scorecards',
      items: [
        { type: 'link', text: 'Scorecards', href: '/dashboard/scorecard' },
        { type: 'link', text: 'Watchlist', href: '/dashboard/scorecard/watchlist' },
        { type: 'link', text: 'Monitoring', href: '/dashboard/scorecard/monitor' },
        ...(canManageScorecards
          ? [{ type: 'link' as const, text: 'Thresholds', href: '/dashboard/scorecard/thresholds' }]
          : []),
        { type: 'link', text: 'Chains', href: '/dashboard/chains' },
        { type: 'link', text: 'Distributors', href: '/dashboard/distributors' },
        { type: 'link', text: 'Create', href: '/create', external: true },
        ...(canManageScorecards
          ? [{ type: 'link' as const, text: 'Bulk Jobs', href: '/dashboard/bulk-jobs' }]
          : []),
      ],
    },
    { type: 'divider' },
    {
      type: 'section',
      text: 'Drip Campaign',
      items: [
        { type: 'link', text: 'Dashboard', href: '/dashboard/drip/dashboard' },
        { type: 'link', text: 'Campaigns', href: '/dashboard/drip/campaigns' },
        { type: 'link', text: 'Emails', href: '/dashboard/drip/emails' },
        { type: 'link', text: 'Contacts', href: '/dashboard/drip/contacts' },
      ],
    },
    { type: 'divider' },
    {
      type: 'section',
      text: 'Zip Blasts',
      items: [
        { type: 'link', text: 'All blasts', href: '/dashboard/zip-blasts' },
        { type: 'link', text: 'New blast', href: '/dashboard/zip-blasts/new' },
      ],
    },
    { type: 'divider' },
    {
      type: 'section',
      text: 'Voice Sales',
      items: [
        { type: 'link', text: 'Dashboard', href: '/dashboard/voice-sales' },
        { type: 'link', text: 'Agent', href: '/dashboard/voice-sales/agent' },
        { type: 'link', text: 'Campaigns', href: '/dashboard/voice-sales/campaigns' },
        { type: 'link', text: 'Calls', href: '/dashboard/voice-sales/calls' },
      ],
    },
  ];

  return (
    <SideNavigation
      activeHref={dashboardActiveHref(location.pathname)}
      className="side-navigation"
      items={sideItems}
      onFollow={(event) => {
        if (event.detail.external) {
          return;
        }
        event.preventDefault();
        navigate({ to: event.detail.href });
      }}
    />
  );
}

export function DashboardTopNavigation() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <TopNavigation
      className="top-navigation"
      identity={{
        title: 'AI Analytics',
        href: '/dashboard',
        logo: {
          src: 'https://cdn.aiescape.io/realtimefeedback/realtime-feedback-footer-logo.png',
          alt: 'Real Time Feedback Logo',
        },
        onFollow: (event) => {
          event.preventDefault();
          navigate({ to: '/dashboard' });
        },
      }}
      utilities={
        user
          ? [
              {
                type: 'menu-dropdown',
                text: user.email,
                iconName: 'user-profile',
                onItemClick: async (event) => {
                  if (event.detail.external) {
                    return;
                  }
                  event.preventDefault();

                  const href = event.detail.href;

                  if (href) {
                    navigate({ to: href });
                  }

                  if (event.detail.id === 'logout') {
                    logout();
                  }
                },
                items: [
                  {
                    id: 'logout',
                    itemType: 'action',
                    text: 'Logout',
                    iconName: 'redo',
                  },
                ],
              },
            ]
          : []
      }
    />
  );
}
